/**
 * Map State Persistence Layer
 *
 * Persists map rendering state to sessionStorage to survive HMR reloads.
 * Prevents markers from flickering or disappearing during development hot reloads.
 *
 * Strategy:
 * - Save layer configurations when map loads successfully
 * - Restore immediately on HMR reload before map re-initialization
 * - Clear on full page reload (sessionStorage auto-clears)
 */

interface MapLayerState {
  layersCreated: boolean;
  iconsLoaded: string[]; // List of loaded icon names
  sourceData: {
    geojson: GeoJSON.FeatureCollection;
  } | null;
  timestamp: number;
}

const SESSION_KEY = 'map_layer_state';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes (survive HMR reloads within dev session)

// Cheap proxy for the ~2MB sessionStorage budget. Large fleets embed the full
// unit in every feature (~10KB/feature), so a few hundred features already blow
// past the limit. Bail on feature COUNT before the expensive JSON.stringify
// rather than serializing the whole payload only to discard it — that
// serialize-then-discard blocked the main thread on every map interaction on
// mobile. The post-serialize byte check below remains as a backstop.
const MAX_CACHEABLE_FEATURES = 400;

class MapStateCache {
  /**
   * Save map layer state to sessionStorage
   */
  saveLayerState(state: Omit<MapLayerState, 'timestamp'>): void {
    try {
      // Cheap pre-serialize guard: skip oversized fleets BEFORE stringifying.
      const featureCount = state.sourceData?.geojson?.features?.length ?? 0;
      if (featureCount > MAX_CACHEABLE_FEATURES) {
        console.log(`[MapStateCache] Skipping save - too many features (${featureCount})`);
        return;
      }

      const cacheData: MapLayerState = {
        ...state,
        timestamp: Date.now(),
      };

      const serialized = JSON.stringify(cacheData);

      // Check data size before writing (sessionStorage typically has 5MB limit)
      // Skip caching if data is too large to prevent quota errors
      if (serialized.length > 2 * 1024 * 1024) {
        console.log(`[MapStateCache] Skipping save - data too large (${Math.round(serialized.length / 1024)}KB)`);
        return;
      }

      // Clear existing entry first to free space
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.setItem(SESSION_KEY, serialized);
      console.log(`[MapStateCache] Saved layer state`);
    } catch (error) {
      // Handle quota errors gracefully - just skip caching
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('[MapStateCache] Storage quota exceeded, clearing cache');
        try {
          sessionStorage.removeItem(SESSION_KEY);
        } catch {
          // Ignore cleanup errors
        }
      } else {
        console.warn('[MapStateCache] Failed to save layer state:', error);
      }
    }
  }

  /**
   * Restore map layer state from sessionStorage
   * Returns null if cache is expired or doesn't exist
   */
  restoreLayerState(): MapLayerState | null {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);

      if (!cached) {
        console.log(`[MapStateCache] No cached state found`);
        return null;
      }

      const state: MapLayerState = JSON.parse(cached);
      const age = Date.now() - state.timestamp;

      // Check if cache is expired
      if (age > CACHE_TTL) {
        console.log(`[MapStateCache] Cache expired (age: ${Math.round(age / 1000)}s)`);
        this.clearLayerState();
        return null;
      }

      console.log(`[MapStateCache] Restored layer state (age: ${Math.round(age / 1000)}s)`);
      return state;
    } catch (error) {
      console.warn('[MapStateCache] Failed to restore layer state:', error);
      return null;
    }
  }

  /**
   * Clear cached layer state
   */
  clearLayerState(): void {
    try {
      sessionStorage.removeItem(SESSION_KEY);
      console.log(`[MapStateCache] Cleared layer state`);
    } catch (error) {
      console.warn('[MapStateCache] Failed to clear layer state:', error);
    }
  }

  /**
   * Check if layer state exists and is fresh
   */
  hasValidLayerState(): boolean {
    const state = this.restoreLayerState();
    return state !== null && state.layersCreated;
  }
}

// Singleton instance
export const mapStateCache = new MapStateCache();

/**
 * Save GeoJSON source data to prevent data loss on HMR
 * This complements the layer state cache by preserving the actual marker data
 */
export function saveSourceData(geojsonData: GeoJSON.FeatureCollection): void {
  mapStateCache.saveLayerState({
    layersCreated: true,
    iconsLoaded: [], // Will be populated by layer management hook
    sourceData: {
      geojson: geojsonData,
    },
  });
}

/**
 * Mark icons as loaded in cache
 */
export function markIconsLoaded(iconNames: string[]): void {
  const existing = mapStateCache.restoreLayerState();
  if (existing) {
    mapStateCache.saveLayerState({
      ...existing,
      iconsLoaded: iconNames,
    });
  }
}

/**
 * Check if a specific icon is cached as loaded
 */
export function isIconCached(iconName: string): boolean {
  const state = mapStateCache.restoreLayerState();
  return state?.iconsLoaded.includes(iconName) ?? false;
}
